import { useEffect, useState } from 'react';
import type { ApiState } from '@/types';

/**
 * Custom hook for API calls with automatic cleanup and cancellation
 */
export function useApi<T>(
  apiCall: () => Promise<T>,
  dependencies: any[] = []
): ApiState<T> {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    const executeApiCall = async () => {
      try {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        const data = await apiCall();
        
        if (!cancelled) {
          setState({ data, loading: false, error: null });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            data: null,
            loading: false,
            error: error instanceof Error ? error : new Error('Unknown error'),
          });
        }
      }
    };

    executeApiCall();

    return () => {
      cancelled = true;
    };
  }, dependencies);

  return state;
}

/**
 * Specific hook for header data
 */
export function useHeaderData() {
  return useApi(async () => {
    const { fetchHeaderData } = await import('@/lib/api/client');
    return fetchHeaderData();
  }, []);
}